import { useCallback } from 'react';
import { useNavigate } from 'react-router';
import type { NotificationAction, NotificationItem } from './api';

interface Handlers {
  markRead: (item: NotificationItem) => void;
  runAction: (item: NotificationItem, key: string) => Promise<boolean>;
  /** Called after navigating away (the bell closes its panel). */
  onNavigate?: () => void;
}

/**
 * What clicking a notification, or one of its buttons, does -- shared by the bell and the page
 * so the two cannot drift. Opening marks it (and any folded repeats) read and follows the link,
 * unless the thing it points at is gone, in which case it only marks read: following the link
 * would end on an error page.
 */
export function useNotificationOpen({ markRead, runAction, onNavigate }: Handlers) {
  const navigate = useNavigate();

  const open = useCallback((item: NotificationItem, more: NotificationItem[] = []) => {
    [item, ...more].forEach((n) => { if (!n.is_read) markRead(n); });
    if (item.available !== false && item.action_url) {
      onNavigate?.();
      navigate(item.action_url);
    }
  }, [markRead, navigate, onNavigate]);

  const act = useCallback((item: NotificationItem, action: NotificationAction) => {
    if (action.kind === 'link' && action.url) {
      if (!item.is_read) markRead(item);
      onNavigate?.();
      navigate(action.url);
      return;
    }
    runAction(item, action.key);
  }, [markRead, navigate, onNavigate, runAction]);

  return { open, act };
}
